import { computed, inject, Injectable, OnDestroy, signal } from "@angular/core";
import { Router } from "@angular/router";

import { firstValueFrom, Observable, of, Subscription, timer } from "rxjs";
import { catchError, tap } from "rxjs/operators";

import { AIUsage } from "@admin/components/ai-usage/models/ai-usage.model";
import { LoggerService } from "@core/services/logger.service";
import { UserPreferencesService } from "@core/services/user-preferences.service";
import { RegisterInvitedDto, RegisterLocalDto } from "@login/models/register.model";
import { ModalService } from "@shared/components/modal/modal.service";
import { AuthProvider } from "@shared/models/types.model";
import { User } from "@shared/models/user.model";

import { AuthHttpService } from "./auth-http.service";

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

@Injectable({
  providedIn: "root"
})
export class AuthService implements OnDestroy {
  readonly currentUser = signal<User | null>(null);
  readonly isLoggedIn = computed(() => this.currentUser() !== null);

  readonly isImpersonating = signal<boolean>(!!inject(UserPreferencesService).getRecoveryToken());

  private _accessToken: string | null = null;
  private _refreshTimerSubscription: Subscription | null = null;
  private readonly _tokenRefreshBuffer = 60000; // 1 minute buffer before expiration
  private readonly _logger = inject(LoggerService);
  private readonly _authHttpService = inject(AuthHttpService);
  private readonly _router = inject(Router);
  private readonly _modalService = inject(ModalService);
  private readonly _userPreferencesService = inject(UserPreferencesService);

  get accessToken(): string | null {
    return this._accessToken;
  }

  set accessToken(token: string | null) {
    this._accessToken = token;

    // Clear any existing timer
    this.clearRefreshTimer();

    // If we have a token, set up the refresh timer
    if (token) {
      this.setupRefreshTimer(token);
    }
  }

  async initializeAuth(): Promise<boolean> {
    try {
      // Try to refresh token first since access token is lost on reload
      const tokens = await firstValueFrom(this._authHttpService.refresh());
      this.accessToken = tokens.accessToken;

      // Then get user data
      const user = await firstValueFrom(this._authHttpService.getMe());
      this.currentUser.set(user);

      // Check for upgrade param and show modal
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("upgraded") === "true") {
        // show billing modal
        this._modalService.openBillingModal(this.currentUser()?.hasPlan ?? false);
        // Clean up URL
        this._router.navigate([], {
          queryParams: { upgraded: null },
          queryParamsHandling: "merge"
        });
      }

      return true;
    } catch {
      this.currentUser.set(null);
      return false;
    }
  }

  getMe(): Observable<User | null> {
    return this._authHttpService.getMe();
  }

  getUsage(): Observable<AIUsage> {
    return this._authHttpService.getUsage();
  }

  async updateCurrentUser(): Promise<User | null> {
    if (this.shouldRefreshToken()) {
      await this.refresh();
    }

    try {
      const user = await firstValueFrom(this._authHttpService.getMe());
      this.currentUser.set(user);
      return user;
    } catch {
      this.currentUser.set(null);
      return null;
    }
  }

  loginWithProvider(provider: AuthProvider, organizationId?: string | null): Observable<string> {
    return this._authHttpService.loginWithProvider(provider, organizationId);
  }

  loginWithProviderForUpgrade(provider: AuthProvider): Observable<string> {
    return this._authHttpService.loginWithProviderForUpgrade(provider);
  }

  checkEmail(email: string): Observable<{ exists: boolean }> {
    return this._authHttpService.checkEmail(email);
  }

  requestPasswordReset(email: string): Observable<{ success: boolean; message: string }> {
    return this._authHttpService.requestPasswordReset(email);
  }

  verifyResetToken(token: string, email: string): Observable<{ success: boolean; message: string }> {
    return this._authHttpService.verifyResetToken(token, email);
  }

  resetPassword(token: string, email: string, password: string): Observable<{ success: boolean; message: string }> {
    return this._authHttpService.resetPassword(token, email, password);
  }

  verifyEmail(token: string, email: string): Observable<{ success: boolean; message: string; alreadyConfirmed: boolean }> {
    return this._authHttpService.verifyEmail(token, email);
  }

  resendEmailVerification(email: string): Observable<{ success: boolean; message: string }> {
    return this._authHttpService.resendEmailVerification(email);
  }

  loginWithCredentials(username: string, password: string): Observable<TokenResponse> {
    return this._authHttpService.loginWithCredentials(username, password).pipe(
      tap((tokens: TokenResponse) => {
        this.accessToken = tokens.accessToken;
        this.updateCurrentUser().catch((error) => {
          this._logger.error("Failed to update user after login:", error);
        });
      })
    );
  }

  logout(): Observable<{ success: boolean }> {
    return this._authHttpService.logout().pipe(
      tap(() => {
        this.clearUserAndToken();
      }),
      catchError(() => {
        this.clearUserAndToken();
        return of({ success: false });
      })
    );
  }

  deleteAccount(): Observable<void> {
    return this._authHttpService.deleteAccount().pipe(tap(() => this.clearUserAndToken()));
  }

  updateProfile(firstName?: string, lastName?: string): Observable<User> {
    return this._authHttpService.updateProfile(firstName, lastName).pipe(
      tap((user: User) => {
        // Update current user without triggering ID change
        this.currentUser.set(user);
      })
    );
  }

  updatePreferences(preferences: any): Observable<User> {
    return this._authHttpService.updatePreferences(preferences).pipe(
      tap((user: User) => {
        // Update current user without triggering ID change
        this.currentUser.set(user);
      })
    );
  }

  uploadAvatar(file: File): Observable<User> {
    return this._authHttpService.uploadAvatar(file).pipe(
      tap((user: User) => {
        this.currentUser.set(user);
      })
    );
  }

  deleteAvatar(): Observable<User> {
    return this._authHttpService.deleteAvatar().pipe(
      tap((user: User) => {
        this.currentUser.set(user);
      })
    );
  }

  async refresh(): Promise<void> {
    this._logger.debug(`Refreshing token. Starting at ${Date.now()}`);
    const tokens = await firstValueFrom(this._authHttpService.refresh());
    this.accessToken = tokens.accessToken;
    await this.updateCurrentUser();
    this._logger.debug(`Tokens refreshed at ${Date.now()}`, tokens);
  }

  ngOnDestroy(): void {
    this.clearRefreshTimer();
  }

  registerInvitedUser(data: RegisterInvitedDto): Observable<void> {
    return this._authHttpService.registerInvitedUser(data);
  }

  registerLocal(data: RegisterLocalDto): Observable<TokenResponse> {
    return this._authHttpService.registerLocal(data).pipe(
      tap((tokens: TokenResponse) => {
        this.accessToken = tokens.accessToken;
        this.updateCurrentUser().catch((error) => {
          this._logger.error("Failed to update user after registration:", error);
        });
      })
    );
  }

  async ensureGuest(): Promise<boolean> {
    try {
      const tokens = await firstValueFrom(this._authHttpService.createGuest());
      this.accessToken = tokens.accessToken;
      const user = await firstValueFrom(this._authHttpService.getMe());
      this.currentUser.set(user);
      return true;
    } catch (e) {
      this._logger.warn("Guest creation failed", e as any);
      return false;
    }
  }

  upgradeFromGuest(payload: { email: string; password: string; username?: string }): Observable<TokenResponse> {
    return this._authHttpService.upgradeFromGuest(payload).pipe(
      tap((tokens: TokenResponse) => {
        this.accessToken = tokens.accessToken;
        this.updateCurrentUser().catch((error) => {
          this._logger.error("Failed to update user after upgrade:", error);
        });
      })
    );
  }

  private clearUserAndToken(): void {
    this.currentUser.set(null);
    this.accessToken = null;
    this._userPreferencesService.clearRecoveryToken();
    this.isImpersonating.set(false);

    // Note: WebSocket disconnection should be handled by DocumentAiHttpService
    // listening to authentication state changes or explicit disconnect calls
    // from components during logout flow
  }

  async impersonate(userId: string): Promise<void> {
    const currentToken = this.accessToken;
    if (currentToken && !this.isImpersonating()) {
      this._userPreferencesService.setRecoveryToken(currentToken);
      this.isImpersonating.set(true);
    }

    try {
      const tokens = await firstValueFrom(this._authHttpService.impersonate(userId));
      this.accessToken = tokens.accessToken;
      await this.updateCurrentUser();
      this._router.navigate(["/"]);
    } catch (e) {
      // Revert if failed and we just set it
      if (currentToken && this.isImpersonating()) {
        this._userPreferencesService.clearRecoveryToken();
        this.isImpersonating.set(false);
      }
      throw e;
    }
  }

  async stopImpersonating(): Promise<void> {
    const recoveryToken = this._userPreferencesService.getRecoveryToken();
    if (!recoveryToken) return;

    // Check if recovery token is expired
    if (this.shouldRefreshToken(recoveryToken)) {
      this._logger.warn("Impersonation recovery token expired. Logging out.");
      this.logout().subscribe();
      return;
    }

    // Set access token to recovery token so API call uses it
    this.accessToken = recoveryToken;

    try {
      const tokens = await firstValueFrom(this._authHttpService.revertImpersonation());
      this.accessToken = tokens.accessToken;
      this._userPreferencesService.clearRecoveryToken();
      this.isImpersonating.set(false);
      await this.updateCurrentUser();
      this._router.navigate(["/"]);
    } catch (e) {
      this._logger.error("Failed to stop impersonating", e);
      // If failed, force logout
      this.logout().subscribe();
    }
  }

  private shouldRefreshToken(token: string | null = this.accessToken): boolean {
    if (!token) {
      return false;
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiresIn = payload.exp * 1000 - Date.now();
      // Refresh if token expires in less than 1 minute
      return expiresIn < this._tokenRefreshBuffer;
    } catch {
      return false;
    }
  }

  private getTokenExpirationTime(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch {
      return null;
    }
  }

  private setupRefreshTimer(token: string): void {
    const expirationTime = this.getTokenExpirationTime(token);

    if (!expirationTime) {
      this._logger.error("Could not determine token expiration time");
      return;
    }

    // Calculate time until refresh (expiration time minus buffer)
    const timeUntilRefresh = Math.max(0, expirationTime - Date.now() - this._tokenRefreshBuffer);

    this._logger.debug(`Setting up token refresh timer for ${timeUntilRefresh}ms from now`);

    // Set up the timer to refresh the token
    this._refreshTimerSubscription = timer(timeUntilRefresh).subscribe(() => {
      this._logger.debug("Token refresh timer triggered");
      if (this.accessToken) {
        this.refresh().catch((error) => {
          this._logger.error("Automatic token refresh failed:", error);
        });
      }
    });
  }

  private clearRefreshTimer(): void {
    if (this._refreshTimerSubscription) {
      this._refreshTimerSubscription.unsubscribe();
      this._refreshTimerSubscription = null;
    }
  }
}
